/**
 * ノート一覧の取得・ページング・無限スクロール。
 *
 * タイムライン（docs/01-requirements.md §5.6）と一覧（§5.17）が共有する。
 * **表示の仕方はここに持ち込まない**（折りたたみ・行の描き方は各ビューの責任）。
 *
 * 落とし穴の対策を 3 つ抱えている。動かすときはどれも外さないこと:
 * - **世代番号**: 絞り込みを続けて変えたとき、古いリクエストの結果が後着して混ざるのを防ぐ。
 *   現象が地味で気づきにくい。
 * - **多重実行の防止**: IntersectionObserver は連続で発火し得る。
 * - **失敗したら打ち切る**: 失敗したページを無限に叩き続けない。
 */

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { api } from '@app/api/client';
import type { ViewState } from '@app/views/view-preference';
import type { NoteSummary } from '@shared/types';

export interface NoteFeed {
  notes: NoteSummary[];
  loading: boolean;
  /** これ以上読むものが無い（または失敗して打ち切った）。 */
  done: boolean;
  error: unknown;
  /** 失敗からの再試行。0 件なら先頭から、途中まで読めていれば続きから。 */
  reload: () => void;
  /** 継ぎ足しの起点。ビュー側で一覧の末尾に置く。 */
  sentinelRef: RefObject<HTMLDivElement | null>;
}

export function useNoteFeed(view: ViewState, pageSize: number): NoteFeed {
  const [notes, setNotes] = useState<NoteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const cursorRef = useRef<string | null>(null);
  const loadingRef = useRef(false);
  /** 絞り込み変更で古いリクエストの結果が混ざらないようにする世代番号。 */
  const generationRef = useRef(0);

  const { sort, categoryId } = view;

  const loadPage = useCallback(
    async (reset: boolean) => {
      if (loadingRef.current) return;
      const generation = generationRef.current;
      loadingRef.current = true;
      setLoading(true);
      try {
        const result = await api.listNotes({
          sort,
          limit: pageSize,
          ...(categoryId != null ? { category_id: categoryId } : {}),
          ...(reset || !cursorRef.current ? {} : { cursor: cursorRef.current }),
        });
        if (generation !== generationRef.current) return;
        setNotes((prev) => (reset ? result.notes : [...prev, ...result.notes]));
        cursorRef.current = result.next_cursor;
        setDone(result.next_cursor === null);
        setError(null);
      } catch (err: unknown) {
        if (generation !== generationRef.current) return;
        setError(err);
        // 失敗したページを無限に叩き続けないよう、いったん打ち切る。
        setDone(true);
      } finally {
        if (generation === generationRef.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [sort, categoryId, pageSize],
  );

  // 絞り込み・並び順が変わったら先頭から読み直す。
  useEffect(() => {
    generationRef.current += 1;
    cursorRef.current = null;
    loadingRef.current = false;
    setNotes([]);
    setDone(false);
    setError(null);
    void loadPage(true);
  }, [loadPage]);

  // 無限スクロール。
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || done) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void loadPage(false);
      },
      { rootMargin: '400px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [done, loadPage, notes.length]);

  const reload = useCallback(() => {
    void loadPage(notes.length === 0);
  }, [loadPage, notes.length]);

  return { notes, loading, done, error, reload, sentinelRef };
}
